/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import RenderDocument from "App/render_document";
import View from "App/view/view";
import ViewControls from "App/view/controls/view_controls";
import Camera from "App/view/cameras/camera";
import {mat4, vec2, vec3, vec4} from "gl-matrix";
import {FLOAT_PRECISION} from "App/math";
import {EViewControls, EViewControlsFamily} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

/**
 * Controls behaviour for view.
 *
 * Camera cannot be stored here (just used) because we want to be able to change camera type on the fly and
 * don't want controls to be dependent on it.
 *
 * @param {object} aDocument render document
 * @param {object} aView View instance
 * @extends ViewControls
 * @constructor
 */
export default
class ExaminerControls extends ViewControls {

    protected _lastPickedPoints: vec3[] = [];
    protected _firstPersonMode: boolean = false;
    protected _wasFirstPersonMode: boolean = false;
    protected moveFlagMap: any = {};

    protected _panByKeyboard: any = {
        initPan: true,
        stopPan: false,
        pickedPoint: vec3.create(),
        currPanPoint: vec2.create()
    };

    protected _zoomSwitchingTimeout: number = 100;
    protected _zoomed: boolean = false;
    protected _zoomGhostCallbackScheduled: boolean = false;
    protected _zoomGhostCallback: any = null;

    private  _assets: any[] = []; // remove, only for testing add/remove asets
    private  _yRotation: number = 0;

    constructor(aDocument: RenderDocument, aView: View) {
        super(aDocument, aView);

        this._family = EViewControlsFamily.EXAMINER;
        this._camera = new Camera(this._view);
        this._initCameraParams();

        this.goForward = 0;
        this.goBack = 1;
        this.goLeft = 2;
        this.goRight = 3;
        this.goUp = 4;
        this.goDown = 5;

        this.moveFlagMap = [];
        this.moveFlagMap[this.goForward] = false;
        this.moveFlagMap[this.goBack] = false;
        this.moveFlagMap[this.goLeft] = false;
        this.moveFlagMap[this.goRight] = false;
        this.moveFlagMap[this.goUp] = false;
        this.moveFlagMap[this.goDown] = false;

        this._zoomGhostCallback = () => {
            if (this._zoomed) {
                this._zoomGhostCallbackScheduled = false;
                this._mouseState.isMoving = false;
                this._sceneAPI.viewModeModule.scheduleRedraw();
            } else {
                this._zoomGhostCallbackScheduled = true;

                this._zoomed = false;
                setTimeout(this._zoomGhostCallback, this._zoomSwitchingTimeout);
            }
        };

        // extend pgc from superclass
        this._pgc.pickedPointMs = vec3.create();
        this._pgc.zoomedWindowPoint = vec4.create();
        this._pgc.target = vec3.create();
    }

    public getControlsType(): EViewControls {
        return EViewControls.EXAMINER;
    }

    public getLastPickedPoint(): vec3 | null {
        if (this._lastPickedPoints.length > 0) {
            return this._lastPickedPoints[0];
        }

        return null;
    }

    public rotate(aRadX: number, aRadY: number) {

        // this is for VR to know the init rotation, we should later replace it by computing the rotation when we start VR mode

        // here we allow rotation only around y axis as we wolk like to allow the users with headset to move forward without
        // turning their head around
        let factor = this.mouseSensitivity;
        let wRad = aRadX / (factor * 0.3);

        // this._camera.changeInitYOrientation(wRad);
        this._cumulYRot += wRad;
        if (this._cumulYRot > 2 * Math.PI) {
            this._cumulYRot -= 2 * Math.PI;
        }
        else if (this._cumulYRot < 0) {
            this._cumulYRot += 2 * Math.PI;
        }

        // end VR section

        let cameraEye = this._camera.getCameraPosition();
        let cameraTarget = this._camera.getCameraTarget();
        let cameraUpVector = this._camera.getCameraUpVectorRef();

        // determine axes
        let view = vec3.create();
        let right = vec3.create();

        vec3.subtract(view, cameraTarget, cameraEye);
        vec3.normalize(view, view);

        vec3.cross(right, view, cameraUpVector);
        vec3.normalize(right, right);

        // move to origin rotate x rotate y move back to target
        let identity = mat4.create();
        mat4.identity(identity);

        let viewMatrix = mat4.create();
        mat4.translate(viewMatrix, identity, cameraTarget);

        mat4.rotate(viewMatrix, viewMatrix, -aRadY, right);
        mat4.rotate(viewMatrix, viewMatrix, -aRadX, cameraUpVector);

        let targetNeg = vec3.create();
        vec3.negate(targetNeg, cameraTarget);
        mat4.translate(viewMatrix, viewMatrix, targetNeg);

        let newEye = vec3.create();
        vec3.transformMat4(newEye, cameraEye, viewMatrix);

        let eyeRestricted = this._camera.restrictPitch(cameraEye, newEye, aRadX, aRadY);
        this._camera.setCameraPosition(eyeRestricted);
    }

    /**
     *  First person look-out (rotate camera target with mouse).
     *  When ended, camera target must be reinialised with pick.
     */
    public rotateCameraTarget(aRadX: number, aRadY: number) {
        // here we allow rotation only around y axis as we wolk like to allow the users with headset to move forward without
        // turning their head around
        let factor = this.mouseSensitivity;
        let wRad = aRadX / (factor * 0.3);

        // this._camera.changeInitYOrientation(wRad);
        this._cumulYRot += wRad;

        super.rotateCameraTarget(aRadX, aRadY);
    }

    public pan(aX: number, aY: number, aDx: number, aDy: number, aPickedPoint: vec3) {
        let cameraEye = this._camera.getCameraPosition();
        let cameraTarget = this._camera.getCameraTarget();

        let pickedPointMs = this._view.projectWindowPointTo3DPoint(aX, aY, this._view.getDepthValueOfPoint(aPickedPoint));

        let mvMatrix = this._camera.getViewMatrix();
        mat4.invert(this._pgc.mat4Inverted, mvMatrix);

        // get 3D point in eye space (space of the camera)
        let pickedPointEs = vec3.create();
        vec3.transformMat4(pickedPointEs, pickedPointMs, mvMatrix);

        // just use this to make the offset dependent on distance between camera and target, because for e.g. 1.0 offset
        // we have float precision problems when determining the offseted window point
        let ETlength = Math.ceil(vec3.length(this._camera.getViewVector()));

        // compute a 3D point shifted along x and y axes
        let shiftedPointMs = vec3.create();
        let shiftedPointEs = vec3.fromValues(pickedPointEs[0] + ETlength, pickedPointEs[1] + ETlength, pickedPointEs[2]);
        vec3.transformMat4(shiftedPointMs, shiftedPointEs, this._pgc.mat4Inverted);

        // project the shifted point to screen coordinates
        let windowPoint = this._view.projectPointToWindow(shiftedPointMs);

        // compute different between the picked and shifted point in the screen space
        let diff = vec2.fromValues(windowPoint[0] - aX, windowPoint[1] - aY);

        // scale the input mouse dx and dy according to the size of the difference of the picked and shifted points in screen space
        let scaledDx = Math.abs(diff[0]) > FLOAT_PRECISION ? aDx / (diff[0] / ETlength) : 0;
        let scaledDy = Math.abs(diff[1]) > FLOAT_PRECISION ? aDy / (diff[1] / ETlength) : 0;

        // transform the scaled dx and dy to model space so that after applying the model view matrix, the picked point will be still after the cursor
        let ddx = vec4.create();
        vec4.transformMat4(ddx, vec4.fromValues(-scaledDx, -scaledDy, 0, 0), this._pgc.mat4Inverted);

        // move both eye and target to simulate the pan effect
        vec3.add(cameraEye, cameraEye, vec3.fromValues(ddx[0], ddx[1], ddx[2]));
        vec3.add(cameraTarget, cameraTarget, vec3.fromValues(ddx[0], ddx[1], ddx[2]));

        this._camera.setCameraPositionAndTarget(cameraEye, cameraTarget);
    }

    public zoom(aX: number, aY: number, aDelta: number, aPickedPosition: vec3) {
        let cameraPos = this._camera.getCameraPosition();

        let pinchSpeedMultiplier = 0.01 * this._sceneAPI.renderSettingsModule.getQualityPerformanceFactor();

        let sign = (aDelta >= 0) ? 1.0 : -1.0;

        let distance = vec3.distance(cameraPos, aPickedPosition);
        let acceleration = aDelta * distance * pinchSpeedMultiplier;
        let zoomDir = this._camera.getCameraDirection();

        if ((distance - acceleration) < 1.64 && aDelta > 0.0) {
            if (distance > 0.001) {
                vec3.scale(zoomDir, zoomDir, distance / 20.0);
                vec3.add(cameraPos, cameraPos, zoomDir);
            }
        } else {
            if (Math.abs(acceleration) > 10000.0) {
                acceleration = sign * 10000.0;
            }
            vec3.scale(zoomDir, zoomDir, acceleration);
            vec3.add(cameraPos, cameraPos, zoomDir);
        }
        this._camera.setCameraPosition(cameraPos);

        // move also the target, because otherwise when the acceleration is behind the target, the view direction will change
        if (sign > 0) {
            let dist = vec3.distance(this._camera.getCameraPosition(), this._camera.getCameraTarget());
            if (vec3.length(zoomDir) > dist) {
                vec3.add(this._pgc.target, cameraPos, zoomDir);
            } else {
                vec3.subtract(this._pgc.target, this._camera.getCameraTarget(), zoomDir);
            }
            this._camera.setCameraTarget(this._pgc.target);
        }

        this.restrictCameraToGround();

        let zoomedWindowPoint = vec4.clone(this._view.projectPointToWindow(aPickedPosition));

        let diff = vec2.create();
        vec2.subtract(diff, vec2.fromValues(zoomedWindowPoint[0], zoomedWindowPoint[1]), vec2.fromValues(aX, aY));

        this.pan(aX, aY, -diff[0], -diff[1], this._view.projectWindowPointTo3DPoint(zoomedWindowPoint[0], zoomedWindowPoint[1], zoomedWindowPoint[2]));

        if (Math.abs(zoomedWindowPoint[2]) > FLOAT_PRECISION) {
            let depth = this._view.getDepthValueOfPoint(aPickedPosition);

            // here we need as an input the center of the view in 2d coordinate system defined by mounse in center of canvas
            let centerWindowPoint = this._view.getCenterWindowPoint();
            this._pgc.pickedPoint = this._view.projectWindowPointTo3DPoint(
                centerWindowPoint[0],
                centerWindowPoint[1],
                depth
            );

            // this is not nice solution here and the zoom implementation should be done in camera or we should
            // have a ExaminerConrolsOrtho with different zoom computation
            // move the eye so that the distance is tha same for ortho camera
            if (this._camera.getUseOrthographicProjection()) {
                // we have to keep the distance from eye and target in order to not change the zoom as it was already
                // changed upper via set the camera positions and corrected via pan
                // so we keep the new target here and keep the distance by moving eye
                let dist = vec3.distance(this._camera.getCameraPosition(), this._camera.getCameraTarget());
                let cameraDirection = vec3.clone(this._camera.getCameraDirection());
                this._camera.setCameraTarget(this._pgc.pickedPoint);
                let newEye = vec3.create();
                vec3.scaleAndAdd(newEye, this._pgc.pickedPoint, cameraDirection, -dist);
                this._camera.setCameraPosition(newEye);
            } else {
                this._camera.setCameraTarget(this._pgc.pickedPoint);
            }
        }

        this._camera._invalidatePerspectiveProjMatrix();
    }

    public animateTo(aX: number, aY: number, aFactor: number, aPickedPoint: vec3) {
        // code below moved to _sceneAPI.viewModeModule.focusToPosition
        // let cameraPos = vec3.clone(this._camera.getCameraPosition());
        // let pickedPointMs = aPickedPoint;
        // let factor = aFactor;
        //
        // let direction = vec3.create();
        // vec3.subtract(direction, pickedPointMs, cameraPos);
        //
        // let distance = vec3.length(direction);
        // let insideBB = this._view.getDocument().getBoundingBox().containsPoint(this._camera.getCameraPosition());
        //
        // if (insideBB && distance <= 2 * this._camera.getNearValue()) {
        //     return;
        // } // prevent near plane clipping when inside model BBox
        //
        // let space = distance * (1.0 - aFactor);
        //
        // if (space <= this._camera.getNearValue()) {
        //     factor = (this._camera.getNearValue() / distance) - 0.05; // go not so close for sure
        // }
        //
        // vec3.scale(direction, direction, factor);
        // vec3.add(cameraPos, cameraPos, direction);
        //
        // this._viewMode.animateView(cameraPos, pickedPointMs, this._camera.getFieldOfView(), 1.0, null);
        /// code below moved to _sceneAPI.viewModeModule.focusToPosition

        this._sceneAPI.viewModeModule.focusToPosition(aPickedPoint, aFactor, 1.0);
        this.restrictCameraToGround();
    }

    public isPanning(buttonState: number): boolean {
        let panButtons = this.getPanButtons();

        let ret = false;
        for (let button of panButtons) {
            ret = ret || ((buttonState & button) === button);
        }

        return ret;
    }

    /***
     *
     * @param x phys mouse x position i.e. the position in real render size
     * @param y phys mouse x position i.e. the position in real render size
     * @param buttonState
     * @returns {boolean}
     */
    public onMousePress(x: number, y: number, buttonState: number) {
        if (!this.isEnabled()) {
            return false;
        }

        if (this.isPanning(buttonState)) {
            this._lastPickedPoints = this._document.renderForValues(this._view, {
                type: "cdwk_depth",
                readPosition: [x, y]
            });
        }

        this._mouseState.set(0, 0, x, y, x, y, buttonState, true);
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number) {
        if (!this.isEnabled()) {
            return false;
        }

        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, buttonState, false);
    }

    public onMove(x: number, y: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        if (this._mouseState.lastX < 0 || this._mouseState.lastY < 0) {
            this._mouseState.lastX = x;
            this._mouseState.lastY = y;
        }
        this._mouseState.dx = x - this._mouseState.lastX;
        this._mouseState.dy = y - this._mouseState.lastY;
        this._mouseState.lastX = x;
        this._mouseState.lastY = y;

        return false;
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        let dx = x - this._mouseState.lastX;
        let dy = y - this._mouseState.lastY;

        this._mouseState.isMoving = true;

        if (this._firstPersonMode) {
            // left and right
            if (this.isRotating(buttonState)) {
                this.rotateCameraTarget(this._view.horizontalPixelsToRad(dx), this._view.verticalPixelsToRad(dy));
            }
            this._wasFirstPersonMode = true;
        }
        else {
            if (this._wasFirstPersonMode) {
                this._wasFirstPersonMode = false;
                this.pickAndSetCameraTarget();
            }

            // left and right
            if (this.isRotating(buttonState)) {
                if (!this._sceneAPI.pickingModule.isCameraRotationDisabled()) {
                    this.rotate(this._view.horizontalPixelsToRad(dx), this._view.verticalPixelsToRad(dy));
                    this.restrictCameraToGround();
                }
            }

            // middle == pan
            if (this.isPanning(buttonState)) {
                let pickedPoint = this.getLastPickedPoint();
                if (!pickedPoint) {
                    let depth = this._view.getDepthValueOfPoint(this._view.getDocument().getBoundingBox().getCenter());
                    pickedPoint = this._view.projectWindowPointTo3DPoint(x, y, depth);
                }

                this.pan(x, y, dx, dy, pickedPoint);
                this.restrictCameraToGround();
            }

            // wheel
            if (buttonState & this.getWheelButton()) {

                let pickedPoints = this._document.renderForValues(this._view, {
                    type: "cdwk_depth",
                    readPosition: [x, y]
                });

                if (!pickedPoints[0]) {
                    let document = this._view.getDocument();
                    let size = this._view.getSize();

                    // compute the distance so that the zoom will change size of the bbox projected on screen by +-10%
                    let distance = vec3.distance(this._camera.getCameraPosition(), document.getBoundingBox().getCenter());

                    let height = 2 * Math.tan(this._camera.getFieldOfView() / 2) * distance; // visible height
                    let screenSize = (document.getBoundingBox().getDiameter() / height) * Math.max(size[0], size[1]);

                    // formula: height = (this._size / screenSize) * Math.max(this._view.vpsize[0], this._view.vpsize[1]);
                    let requiredScreenSize = screenSize + screenSize * 0.1;
                    distance = ((document.getBoundingBox().getDiameter() / requiredScreenSize) * Math.max(size[0], size[1])) / (2 * Math.tan(this._camera.getFieldOfView() / 2));

                    let zoomedToPoint = vec3.create();
                    vec3.scaleAndAdd(
                        zoomedToPoint,
                        this._camera.getCameraPosition(),
                        this._camera.getCameraDirection(),
                        Math.abs(distance)
                    );

                    pickedPoints = [this._view.projectWindowPointTo3DPoint(x, y, this._view.getDepthValueOfPoint(zoomedToPoint))];

                    // prvent switch view direction by moving position behind target in the zoom call
                    this._camera.setCameraTarget(zoomedToPoint);
                }

                this.zoom(x, y, delta, pickedPoints[0]);
            }
        }

        this._mouseState.dx = dx;
        this._mouseState.dy = dy;

        this._mouseState.lastX = x;
        this._mouseState.lastY = y;

        this._view.viewpointChanged();

        return false;
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        this._zoomed = true;

        this.onDrag(x, y, delta, buttonState);

        if (!this._zoomGhostCallbackScheduled) {
            setTimeout(this._zoomGhostCallback, this._zoomSwitchingTimeout);
            this._zoomGhostCallbackScheduled = true;
        }

        return true;
    }

    public onMouseIn(x: number, y: number, buttonState: number): boolean {
        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);
        return true;
    }

    public onMouseOut(x: number, y: number, buttonState: number): boolean {
        this._mouseState.set(0, 0, x, y, this._mouseState.pressX, this._mouseState.pressY, 0, false);
        return true;
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean): boolean {
        return false;
    }

    public onDoubleClick(x: number, y: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        if (!this._shiftDown){
            return false;
        }

        // TB todo: listener handling - add param to Listeners so that custom app listener added via app._scene.addListener
        // can stop propagation...
        // features according to
        // http://blog.sketchfab.com/post/118862616704/website-update-05-13
        this._lastPickedPoints = this._document.renderForValues(this._view, {
            type: "cdwk_depth",
            readPosition: [x, y]
        });

        let animateToPoint = this.getLastPickedPoint();
        if (animateToPoint) {
            this.animateTo(x, y, 0.7, vec3.clone(animateToPoint));
            this._view.viewpointChanged();
        }

        return true;
        // @note: replace functionality in application
        // else
        // {
        //    // set default view
        //    this._view.resetCameraPosition();
        // }
    }

    public onDoubleTap(x: number, y: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        return this.onDoubleClick(x, y);
    }

    public onTouchStart(x: number, y: number, numTouches: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        return super.onTouchStart(x, y, numTouches);
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        return super.onTouchMove(x, y, numTouches, delta);
    }

    public onTouchEnd(x: number, y: number, remainingTouch: number, aDraggedDuringTouch: boolean): boolean {
        if (!this.isEnabled()) {
            return false;
        }

        return super.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch);
    }

    public onKeyDown(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        let arrowDown = false;
        for (let i in this.moveFlagMap) {
            arrowDown = arrowDown || this.moveFlagMap[i];
        }

        let yRotationMat = mat4.create();

        switch (key) {

            case "Shift": // shift
                this._shiftDown = true;
                this._firstPersonMode = true;
                break;

            case "Control": // ctrl
                this._ctrlDown = true;
                break;

            case  "Up": // up arr
            case  "ArrowUp": // up arr
            case  "W": // W
            case  "w": // w
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goForward] = true;
                    if (!arrowDown && !this._ctrlDown) {
                        this._panByKeyboard.initPan = true;
                    }
                }
                break;

            case  "Down": // down arr
            case  "ArrowDown": // down arr
            case  "S": // S
            case  "s": // s
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goBack] = true;
                    if (!arrowDown) {
                        this._panByKeyboard.initPan = true;
                    }
                }
                break;

            case  "Left": // left arr
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goLeft] = true;
                    if (!arrowDown && !this._ctrlDown) {
                        this._panByKeyboard.initPan = true;
                    }
                }
                break;

            case  "Right": // right arr
            case  "ArrowRight": // right arr
            case  "D": // D
            case  "d": // d
                if (!this._ctrlDown) {
                    this.moveFlagMap[this.goRight] = true;
                    if (!arrowDown && !this._ctrlDown) {
                        this._panByKeyboard.initPan = true;
                    }
                }
                break;

            case  "PageUp": // page up
                this.moveFlagMap[this.goUp] = true;
                break;

            case  "PageDown": // page down
                this.moveFlagMap[this.goDown] = true;
                break;

            case  "l": // l lod debug enable/disable
                this._sceneAPI.globe.enableDebug(!this._sceneAPI.globe.getDebugEnabled());
                break;

            case "i":
                this._assets.push(this._sceneAPI.getDocument().addAsset(
                    "/cadworkViewer_ssh/models/texturedCube/geometryDescription.json",
                    "/cadworkViewer_ssh/models/texturedCube/modelDescription.json",
                    false,
                    mat4.create()));
                break;

            case "o":
                this._sceneAPI.getDocument().removeAsset(this._assets[0]);
                this._assets.splice(0, 1);
                break;

            case "n":
                this._yRotation -= 0.1;
                mat4.rotateY(yRotationMat, yRotationMat, this._yRotation);
                this._assets[this._assets.length - 1].setModelTransformation(yRotationMat);
                break;

            case "m":
                this._yRotation += 0.1;
                mat4.rotateY(yRotationMat, yRotationMat, this._yRotation);
                this._assets[this._assets.length - 1].setModelTransformation(yRotationMat);
                break;

            case "t":
                mat4.translate(yRotationMat, yRotationMat, vec3.fromValues(5, 5, 5));
                this._assets[this._assets.length - 1].setModelTransformation(yRotationMat);
                break;

            default:
                break;
        }
    }

    public onKeyUp(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        switch (key) {
            case "Shift": // shift
                this._shiftDown = false;
                this._firstPersonMode = false;
                break;

            case "Control": // ctrl
                this._ctrlDown = false;
                break;

            case  "Up": // up arr
            case  "ArrowUp": // up arr
            case  "W": // W
            case  "w": // w
                this.moveFlagMap[this.goForward] = false;
                break;

            case  "Down": // down arr
            case  "ArrowDown": // down arr
            case  "S": // S
            case  "s": // s
                this.moveFlagMap[this.goBack] = false;
                break;

            case  "Left": // left arr
            case  "ArrowLeft": // left arr
            case  "A": // A
            case  "a": // a
                this.moveFlagMap[this.goLeft] = false;
                break;

            case  "Right": // right arr
            case  "ArrowRight": // right arr
            case  "D": // D
            case  "d": // d
                this.moveFlagMap[this.goRight] = false;
                break;

            case  "PageUp": // page up
                this.moveFlagMap[this.goUp] = false;
                break;

            case "PageDown": // page down
                this.moveFlagMap[this.goDown] = false;
                break;

            default:
                break;
        }
    }

    public onKeyPress(key: string) {
        if (!this.isEnabled()) {
            return false;
        }

        let evt;
        let cs;
        switch (key) {
            // cdwk
            case  "+": // + (incr. speed)
                evt = document.createEvent("Event");
                evt.initEvent("mousewheel", true, true);
                cs = this._view.getSize();
                evt.pageX = cs[0] / 2;
                evt.pageY = cs[1] / 2;
                evt.wheelDelta = 120; // the 120 is default wheel value used in all major browsers?
                this._view.getHTMLCanvas().dispatchEvent(evt);
                break;
            case  "-": // - (decr. speed)
                evt = document.createEvent("Event");
                evt.initEvent("mousewheel", true, true);
                cs = this._view.getSize();
                evt.pageX = cs[0] / 2;
                evt.pageY = cs[1] / 2;
                evt.wheelDelta = -120; // the -120 is default wheel value used in all major browsers?
                this._view.getHTMLCanvas().dispatchEvent(evt);
                break;
            case "*":  // * show debug log dialog
                // toggleShowDebugConsole = !toggleShowDebugConsole;
                // activate(toggleShowDebugConsole);
                break;
            case "/":  // / show properties hud
                this._sceneAPI.getStateViewer().toggle();
                break;
            case "h": // h test sight point
                // this._sceneAPI.hudModule.setSightMarkEnabled(true);
                // this._sceneAPI.viewModule.setUseOrthographicProjection(!this._camera.getUseOrthographicProjection());
                // this._sceneAPI.viewModule.zoomViewAll();
                break;
            case  "p": // p pick scne api element under the mouse cursor
                /*this._sceneAPI.pickingModule.startElementPicking({});
                let sapielement = this._sceneAPI.pickingModule.pickSAPIElement();
                if (sapielement) {
                }*/
                break;

            default:
        }
    }

    public tick(): void {
        super.tick();

        let arrowDown = false;
        let panButtons;
        let panButton;
        for (let i in this.moveFlagMap) {
            arrowDown = arrowDown || this.moveFlagMap[i];
        }
        if (!arrowDown) {
            if (this._panByKeyboard.stopPan) {
                panButtons = this.getPanButtons();
                if (panButtons.length > 0) {
                    panButton = panButtons[0];
                }
                this.onMousePress(this._panByKeyboard.currPanPoint[0], this._panByKeyboard.currPanPoint[1], panButton);
                this._panByKeyboard.stopPan = false;
            }
            return;
        }

        panButtons = this.getPanButtons();
        if (panButtons.length > 0) {
            panButton = panButtons[0];
        }

        if (this._panByKeyboard.initPan) {
            let size = this._view.getSize();
            let screenCenter = vec2.fromValues(size[0] / 2.0, size[1] / 2.0);
            this._panByKeyboard.currPanPoint = screenCenter;
            this.onMousePress(screenCenter[0], screenCenter[1], panButton);
            this._panByKeyboard.initPan = false;
            this._panByKeyboard.stopPan = true;
            this._panByKeyboard.pickedPoint = this.getLastPickedPoint();
        }

        let panSpeedConst = 5.0; // in %
        if (this.moveFlagMap[this.goForward]) {
            this.pan(this._panByKeyboard.currPanPoint[0], this._panByKeyboard.currPanPoint[0], 0.0, -panSpeedConst, this._panByKeyboard.pickedPoint);
            this._panByKeyboard.currPanPoint[1] -= panSpeedConst;
            this.restrictCameraToGround();
            this._view.viewpointChanged();
        }

        if (this.moveFlagMap[this.goBack]) {
            this.pan(this._panByKeyboard.currPanPoint[0], this._panByKeyboard.currPanPoint[0], 0.0, panSpeedConst, this._panByKeyboard.pickedPoint);
            this._panByKeyboard.currPanPoint[1] = panSpeedConst;
            this.restrictCameraToGround();
            this._view.viewpointChanged();
        }

        if (this.moveFlagMap[this.goLeft]) {
            this.pan(this._panByKeyboard.currPanPoint[0], this._panByKeyboard.currPanPoint[0], -panSpeedConst, 0.0, this._panByKeyboard.pickedPoint);
            this._panByKeyboard.currPanPoint[0] = -panSpeedConst;
            this.restrictCameraToGround();
            this._view.viewpointChanged();
        }

        if (this.moveFlagMap[this.goRight]) {
            this.pan(this._panByKeyboard.currPanPoint[0], this._panByKeyboard.currPanPoint[0], panSpeedConst, 0.0, this._panByKeyboard.pickedPoint);
            this._panByKeyboard.currPanPoint[0] = panSpeedConst;
            this.restrictCameraToGround();
            this._view.viewpointChanged();
        }

        return;
    }

    public setFirstPersonMode(aValue: boolean) {
        this._firstPersonMode = aValue;
    }
}
